import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { SkipToMainLink } from '@/components/a11y/SkipToMainLink';
import { AccessibilityMenu } from '@/components/a11y/AccessibilityMenu';
import { GlobalIconBar, MobileBottomNav, SecondaryNavContent } from './SidebarMenu';
import { MobileDrawer } from '@/components/ui/MobileDrawer';
import { useEffectiveViewport } from '@/hooks/useEffectiveViewport';
import { EntitlementsBootstrap } from '@/components/billing/EntitlementsBootstrap';
import { DeletionPendingBanner } from '@/components/billing/DeletionPendingBanner';
import { LifecycleBanner } from '@/components/billing/LifecycleBanner';
import { BetaPricingBanner } from '@/components/billing/BetaPricingBanner';

interface MainLayoutProps {
  children: ReactNode;
}

/**
 * Main layout wrapper: desktop icon rail; mobile bottom nav + “More” drawer.
 */
export function MainLayout({ children }: MainLayoutProps) {
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const { isWide: isDesktop } = useEffectiveViewport();
  const { t: tn } = useTranslation('nav');

  useEffect(() => {
    if (isDesktop) setMoreMenuOpen(false);
  }, [isDesktop]);

  return (
    <div className="flex h-dvh min-h-0 bg-background text-foreground">
      <SkipToMainLink />
      <AccessibilityMenu />
      <GlobalIconBar />
      <MobileDrawer
        open={moreMenuOpen}
        onOpenChange={setMoreMenuOpen}
        title={tn('more')}
        className="bg-sidebar-1"
      >
        <SecondaryNavContent onNavigate={() => setMoreMenuOpen(false)} />
      </MobileDrawer>
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <EntitlementsBootstrap />
        <DeletionPendingBanner />
        <BetaPricingBanner />
        <LifecycleBanner />
        <main
          id="main-content"
          tabIndex={-1}
          className={clsx(
            'min-h-0 flex-1',
            isDesktop
              ? 'flex flex-col overflow-hidden'
              : 'overflow-y-auto pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))]'
          )}
          role="main"
        >
          {children}
        </main>
        <MobileBottomNav onMore={() => setMoreMenuOpen(true)} />
      </div>
    </div>
  );
}
